import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { toastAction, toastError, toastInfo, toastSuccess } from "@/utils/toast";
import AppHeader from "@/components/AppHeader";
import LeftSidebar, { type ProjectsSidebarKey } from "@/components/MainPage/LeftSidebar";
import ProjectCard, { type ProjectCardData } from "@/components/MainPage/ProjectCard";
import SetThumbnailDialog from "@/components/MainPage/SetThumbnailDialog";
import DropdownSelect from "@/components/ui/DropdownSelect";
import {
  useArchiveProject,
  useCreateProject,
  useProjectDetails,
  useProjects,
  useRestoreProject,
  useStartCopyToProductionWorkflow,
  useStartDuplicateProjectWorkflow,
  useUpdateProject
} from "@/api/api";
import useTransactionPolling from "@/hooks/useTransactionPolling";
import LoadingDialog from "@/components/ui/LoadingDialog";
import { useStore } from "@/contexts/StoreContext";
import RenameProjectDialog from "@/components/ui/RenameEntityDialog";
import ConfirmActionAlert from "@/components/ui/ConfirmActionAlert";
import { projectPanelPath } from "@/types/routes";
import { resolveProjectEntryPath } from "@/services/projectLastVisitedPanel";
import { APP_HEADER_HEIGHT_PX, DEFAULT_TAB_REDIRECT_PATH, DEFAULT_TRANSACTION_INTERVAL_MS, NAV_RAIL_EXPANDED_WIDTH_PX } from "@/utils/constants";
import { useCurrentUserEmail, useProjectListItemPermissions } from "@/hooks/useProjectPermissions";
import { computeProjectPermissions } from "@/utils/projectPermissions";

const SORT_KEYS = ["Last modified", "Last created"] as const;
type SortKeyProps = (typeof SORT_KEYS)[number];

type ContextMenuAction = "open" | "share" | "duplicate" | "rename" | "setThumbnail" | "addToSidebar" | "archive" | "restore" | "copyToProduction";

const ARCHIVE_TOAST_OPTIONS = { position: "bottom-left", autoHideDuration: 6000 } as const;

const LONG_RUNNING_TOAST_OPTIONS = { position: "bottom-left", autoHideDuration: null } as const;

const FIGMA_SUCCESS_TOAST_SX = {
  bgcolor: "#313b32",
  color: "#fff",
  "& .MuiAlert-icon": { color: "#66bb6a" }
} as const;
const FIGMA_ERROR_TOAST_SX = {
  bgcolor: "#422d2b",
  color: "#fff",
  "& .MuiAlert-icon": { color: "#f44336" }
} as const;

const PROJECT_TILE_EXIT = { opacity: 0, scale: 0.9, transition: { duration: 0.175, ease: "easeOut" } } as const;
const PROJECT_TILE_LAYOUT_TRANSITION = { layout: { duration: 0.225, ease: [0.22, 1, 0.36, 1], delay: 0.13 } } as const;

const PAGE_HEADINGS: Record<ProjectsSidebarKey, string> = {
  all: "Projects",
  mine: "My projects",
  editAccess: "Edit access",
  shared: "Shared with me",
  archive: "Archive"
};

const IS_STAGE_ENVIRONMENT = import.meta.env.VITE_PUBLIC_ENV === "stage" || import.meta.env.DEV;

const MainPage = () => {
  const store = useStore();
  const navigate = useNavigate();
  const currentUserEmail = useCurrentUserEmail();
  const [activeKey, setActiveKey] = useState<ProjectsSidebarKey>("all");
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKeyProps>("Last modified");
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false);

  const [contextMenu, setContextMenu] = useState<{
    readonly mouseX: number;
    readonly mouseY: number;
    readonly projectId: string;
  } | null>(null);
  const [renameProjectId, setRenameProjectId] = useState<string | null>(null);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [setThumbnailProjectId, setSetThumbnailProjectId] = useState<string | null>(null);
  const [isSetThumbnailDialogOpen, setIsSetThumbnailDialogOpen] = useState(false);
  const [copyToProductionTransactionId, setCopyToProductionTransactionId] = useState<string | null>(null);
  const [duplicateProjectTransactionId, setDuplicateProjectTransactionId] = useState<string | null>(null);
  const [duplicateConfirmTarget, setDuplicateConfirmTarget] = useState<{ readonly projectId: string; readonly title: string } | null>(null);
  const [copyToProductionConfirmTarget, setCopyToProductionConfirmTarget] = useState<{ readonly projectId: string; readonly title: string } | null>(
    null
  );

  const queryClient = useQueryClient();
  const projectsQuery = useProjects();
  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const archiveProjectMutation = useArchiveProject();
  const restoreProjectMutation = useRestoreProject();
  const startCopyToProductionMutation = useStartCopyToProductionWorkflow();
  const startDuplicateProjectMutation = useStartDuplicateProjectWorkflow();
  const { data: renameProjectDetails } = useProjectDetails(isRenameDialogOpen ? renameProjectId : null);

  const { transactionQuery: copyToProductionTransactionQuery } = useTransactionPolling(copyToProductionTransactionId, {
    intervalMs: DEFAULT_TRANSACTION_INTERVAL_MS
  });
  const { transactionQuery: duplicateProjectTransactionQuery } = useTransactionPolling(duplicateProjectTransactionId, {
    intervalMs: DEFAULT_TRANSACTION_INTERVAL_MS
  });
  const projects = useMemo<readonly ProjectCardData[]>(() => {
    const items = projectsQuery.data ?? [];

    return items.map((p) => ({
      id: p.project_id,
      title: p.name,
      isArchived: p.is_archived ?? false,
      thumbnailUrl: p.thumbnail_url ?? undefined,
      editedTimestamp: p.updated_at ?? p.created_at,
      owner: p.owner,
      isLocked: p.is_locked ?? false,
      lockedBy: p.locked_by,
      sortCreatedAt: p.created_at,
      sortModifiedAt: p.updated_at ?? p.created_at
    }));
  }, [projectsQuery.data]);

  const tabFilteredProjects = useMemo(
    () =>
      projects.filter((p) => {
        if (activeKey === "archive") {
          return p.isArchived;
        }

        if (p.isArchived) {
          return false;
        }

        if (activeKey === "mine" || activeKey === "editAccess") {
          const { isOwner, canWrite } = computeProjectPermissions(currentUserEmail, p.owner, p.isLocked, p.lockedBy);
          return activeKey === "mine" ? isOwner : !isOwner && canWrite;
        }

        return true;
      }),
    [projects, activeKey, currentUserEmail]
  );

  const sortedProjects = useMemo(() => {
    const sortValue = sortKey === "Last created" ? (p: ProjectCardData) => p.sortCreatedAt ?? 0 : (p: ProjectCardData) => p.sortModifiedAt ?? 0;
    return tabFilteredProjects.toSorted((a, b) => sortValue(b) - sortValue(a));
  }, [tabFilteredProjects, sortKey]);

  const filteredProjects = useMemo(() => {
    const q = projectSearchQuery.trim().toLowerCase();
    if (q.length === 0) {
      return sortedProjects;
    }

    return sortedProjects.filter((p) => p.title.toLowerCase().includes(q));
  }, [sortedProjects, projectSearchQuery]);

  const pageHeading = PAGE_HEADINGS[activeKey];

  let emptyProjectsMessage = "No projects yet.";
  if (projectSearchQuery.trim().length > 0) {
    emptyProjectsMessage = "No projects match your search.";
  } else if (activeKey === "archive") {
    emptyProjectsMessage = "There are no archived projects.";
  } else if (activeKey === "mine") {
    emptyProjectsMessage = "You don't own any projects yet.";
  } else if (activeKey === "editAccess") {
    emptyProjectsMessage = "No projects have granted you edit access.";
  }

  const contextMenuProject = useMemo(() => (contextMenu ? projects.find((p) => p.id === contextMenu.projectId) : undefined), [contextMenu, projects]);
  const { canWrite: canWriteContextMenuProject } = useProjectListItemPermissions(
    contextMenuProject
      ? { owner: contextMenuProject.owner, is_locked: contextMenuProject.isLocked, locked_by: contextMenuProject.lockedBy }
      : null
  );

  const onOpenProject = (id: string) => {
    void navigate(resolveProjectEntryPath(id));
  };

  const onClickNewProject = () => {
    setIsNewProjectDialogOpen(true);
  };

  const onProjectContextMenu = (event: React.MouseEvent, projectId: string) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      projectId
    });
  };

  function runArchiveProject(projectId: string, title: string) {
    archiveProjectMutation.mutate(projectId, {
      onSuccess: () => {
        toastAction(`Moved to archive "${title}"`, {
          ...ARCHIVE_TOAST_OPTIONS,
          action: { label: "UNDO", onClick: () => runRestoreProject(projectId, title) }
        });
      },
      onError: () => {
        toastAction(`Couldn't archive "${title}"`, {
          ...ARCHIVE_TOAST_OPTIONS,
          action: { label: "TRY AGAIN", onClick: () => runArchiveProject(projectId, title) }
        });
      }
    });
  }

  function runRestoreProject(projectId: string, title: string) {
    restoreProjectMutation.mutate(projectId, {
      onSuccess: () => {
        toastAction(`Moved to projects "${title}"`, {
          ...ARCHIVE_TOAST_OPTIONS,
          action: { label: "UNDO", onClick: () => runArchiveProject(projectId, title) }
        });
      },
      onError: () => {
        toastAction(`Couldn't restore "${title}"`, {
          ...ARCHIVE_TOAST_OPTIONS,
          action: { label: "TRY AGAIN", onClick: () => runRestoreProject(projectId, title) }
        });
      }
    });
  }

  const copyToProductionTitleRef = useRef<string>("");
  const copyToProductionSourceIdRef = useRef<string>("");
  function runCopyToProduction(projectId: string, title: string) {
    copyToProductionTitleRef.current = title;
    copyToProductionSourceIdRef.current = projectId;
    startCopyToProductionMutation.mutate(projectId, {
      onSuccess: (data) => {
        setCopyToProductionTransactionId(data.transaction_id);
      },
      onError: (error) => {
        toastError(error instanceof Error ? error.message : `Failed to migrate "${title}".`, {
          ...LONG_RUNNING_TOAST_OPTIONS,
          sx: FIGMA_ERROR_TOAST_SX,
          action: { label: "RETRY", onClick: () => runCopyToProduction(projectId, title) }
        });
      }
    });
  }

  const duplicateProjectTitleRef = useRef<string>("");
  const duplicateProjectSourceIdRef = useRef<string>("");
  function runDuplicateProject(projectId: string, title: string) {
    duplicateProjectTitleRef.current = title;
    duplicateProjectSourceIdRef.current = projectId;
    startDuplicateProjectMutation.mutate(projectId, {
      onSuccess: (data) => {
        setDuplicateProjectTransactionId(data.transaction_id);
      },
      onError: (error) => {
        toastError(error instanceof Error ? error.message : `Couldn't duplicate "${title}"`, {
          ...LONG_RUNNING_TOAST_OPTIONS,
          sx: FIGMA_ERROR_TOAST_SX,
          action: { label: "RETRY", onClick: () => runDuplicateProject(projectId, title) }
        });
      }
    });
  }

  const lastCopyToProductionResultRef = useRef<string | null>(null);
  useEffect(() => {
    if (!copyToProductionTransactionId) {
      return;
    }

    const status = copyToProductionTransactionQuery.data?.status;
    if (status !== "completed" && status !== "error") {
      return;
    }

    if (lastCopyToProductionResultRef.current === copyToProductionTransactionId) {
      return;
    }

    lastCopyToProductionResultRef.current = copyToProductionTransactionId;

    if (status === "completed") {
      toastSuccess(`Successfully migrated "${copyToProductionTitleRef.current}" to production`, {
        ...LONG_RUNNING_TOAST_OPTIONS,
        sx: FIGMA_SUCCESS_TOAST_SX,
        action: { label: "GOT IT" }
      });
    } else {
      toastError(`Failed to migrate "${copyToProductionTitleRef.current}".`, {
        ...LONG_RUNNING_TOAST_OPTIONS,
        sx: FIGMA_ERROR_TOAST_SX,
        action: { label: "RETRY", onClick: () => runCopyToProduction(copyToProductionSourceIdRef.current, copyToProductionTitleRef.current) }
      });
    }

    setCopyToProductionTransactionId(null);
  }, [copyToProductionTransactionId, copyToProductionTransactionQuery.data]);

  const lastDuplicateProjectResultRef = useRef<string | null>(null);
  useEffect(() => {
    if (!duplicateProjectTransactionId) {
      return;
    }

    const status = duplicateProjectTransactionQuery.data?.status;
    if (status !== "completed" && status !== "error") {
      return;
    }

    if (lastDuplicateProjectResultRef.current === duplicateProjectTransactionId) {
      return;
    }

    lastDuplicateProjectResultRef.current = duplicateProjectTransactionId;

    if (status === "completed") {
      const completedData = duplicateProjectTransactionQuery.data?.status === "completed" ? duplicateProjectTransactionQuery.data.data : undefined;
      const newProjectId = completedData && typeof completedData === "object" && "id" in completedData ? (completedData as { id?: string }).id : undefined;

      void queryClient.invalidateQueries({ queryKey: ["projects"] });

      toastSuccess(`Successfully duplicated "${duplicateProjectTitleRef.current}".`, {
        ...LONG_RUNNING_TOAST_OPTIONS,
        sx: FIGMA_SUCCESS_TOAST_SX,
        ...(newProjectId ? { action: { label: "OPEN PROJECT", onClick: () => onOpenProject(newProjectId) } } : {})
      });
    } else {
      toastError(`Failed to duplicate "${duplicateProjectTitleRef.current}".`, {
        ...LONG_RUNNING_TOAST_OPTIONS,
        sx: FIGMA_ERROR_TOAST_SX,
        action: {
          label: "RETRY",
          onClick: () => runDuplicateProject(duplicateProjectSourceIdRef.current, duplicateProjectTitleRef.current)
        }
      });
    }

    setDuplicateProjectTransactionId(null);
  }, [duplicateProjectTransactionId, duplicateProjectTransactionQuery.data, queryClient]);

  const handleContextMenuAction = (action: ContextMenuAction) => {
    const projectId = contextMenu?.projectId;
    const projectTitle = contextMenuProject?.title ?? "project";
    setContextMenu(null);
    if (!projectId) {
      return;
    }

    if (action === "open") {
      onOpenProject(projectId);
      return;
    }

    // Archive/restore are exempt: a visibility flag only, not a content mutation, so any
    // collaborator may toggle it even while another user holds the lock.
    const writeGatedActions: ReadonlySet<ContextMenuAction> = new Set(["rename", "setThumbnail"]);
    if (writeGatedActions.has(action) && !canWriteContextMenuProject) {
      toastError("This project is locked by its owner. Only the owner can make changes while it is locked.", { position: "bottom-left" });
      return;
    }

    if (action === "rename") {
      setRenameProjectId(projectId);
      setIsRenameDialogOpen(true);
      return;
    }

    if (action === "setThumbnail") {
      setSetThumbnailProjectId(projectId);
      setIsSetThumbnailDialogOpen(true);
      return;
    }

    if (action === "archive") {
      runArchiveProject(projectId, projectTitle);
      return;
    }

    if (action === "restore") {
      runRestoreProject(projectId, projectTitle);
      return;
    }

    if (action === "duplicate") {
      setDuplicateConfirmTarget({ projectId, title: projectTitle });
      return;
    }

    if (action === "copyToProduction") {
      setCopyToProductionConfirmTarget({ projectId, title: projectTitle });
      return;
    }

    toastInfo(`TODO: ${action} - ${projectId}`);
  };

  const renameInitialName = useMemo(() => {
    const fromGrid = renameProjectId ? projects.find((p) => p.id === renameProjectId)?.title : null;
    return renameProjectDetails?.project_name ?? fromGrid ?? "";
  }, [projects, renameProjectDetails?.project_name, renameProjectId]);

  return (
    <Box sx={{ width: "100vw", height: "100vh", display: "flex", flexDirection: "column" }}>
      <AppHeader height={APP_HEADER_HEIGHT_PX} variant="home" onClickNewProject={onClickNewProject} />
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <LeftSidebar
          headerHeight={APP_HEADER_HEIGHT_PX}
          width={NAV_RAIL_EXPANDED_WIDTH_PX}
          activeKey={activeKey}
          onSelect={setActiveKey}
          searchQuery={projectSearchQuery}
          onSearchQueryChange={setProjectSearchQuery}
        />
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", px: 4, py: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {pageHeading}
            </Typography>
            <Box>
              <DropdownSelect value={sortKey} options={SORT_KEYS} onChange={setSortKey} ariaLabel="Sort projects" />
            </Box>
          </Box>
          <Box
            key={activeKey}
            sx={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 2
            }}
          >
            <LoadingDialog open={projectsQuery.isLoading} />
            {projectsQuery.isError ? (
              <Typography variant="body2" color="error">
                Failed to load projects.
              </Typography>
            ) : null}
            {filteredProjects.length === 0 && !projectsQuery.isLoading && !projectsQuery.isError && projects.length > 0 ? (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {emptyProjectsMessage}
                </Typography>
              </Box>
            ) : null}
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredProjects.map((p) => (
                <motion.div key={p.id} layout exit={PROJECT_TILE_EXIT} transition={PROJECT_TILE_LAYOUT_TRANSITION}>
                  <ProjectCard project={p} onOpen={onOpenProject} onContextMenu={onProjectContextMenu} />
                </motion.div>
              ))}
            </AnimatePresence>
          </Box>
        </Box>
      </Box>
      <Menu
        open={Boolean(contextMenu)}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        {...(contextMenu ? { anchorPosition: { top: contextMenu.mouseY, left: contextMenu.mouseX } } : {})}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              minWidth: 180,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "divider"
            }
          }
        }}
      >
        <MenuItem onClick={() => handleContextMenuAction("open")}>
          <ListItemText primary="Open" />
        </MenuItem>
        <MenuItem disabled onClick={() => handleContextMenuAction("share")}>
          <ListItemText primary="Share" />
        </MenuItem>
        <MenuItem onClick={() => handleContextMenuAction("duplicate")}>
          <ListItemText primary="Duplicate" />
        </MenuItem>
        <Divider />
        <MenuItem disabled={!canWriteContextMenuProject} onClick={() => handleContextMenuAction("rename")}>
          <ListItemText primary="Rename" />
        </MenuItem>
        {IS_STAGE_ENVIRONMENT ? (
          <MenuItem onClick={() => handleContextMenuAction("copyToProduction")}>
            <ListItemText primary="Copy to Production" />
          </MenuItem>
        ) : null}
        <MenuItem disabled={!canWriteContextMenuProject} onClick={() => handleContextMenuAction("setThumbnail")}>
          <ListItemText primary="Set thumbnail" />
        </MenuItem>
        <MenuItem disabled onClick={() => handleContextMenuAction("addToSidebar")}>
          <ListItemText primary="Add to sidebar" />
        </MenuItem>
        {contextMenuProject?.isArchived ? (
          <MenuItem onClick={() => handleContextMenuAction("restore")}>
            <ListItemText primary="Restore project" />
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleContextMenuAction("archive")}>
            <ListItemText primary="Archive project" />
          </MenuItem>
        )}
      </Menu>
      <ConfirmActionAlert
        open={Boolean(duplicateConfirmTarget)}
        onClose={() => setDuplicateConfirmTarget(null)}
        title={`Duplicate "${duplicateConfirmTarget?.title ?? ""}"?`}
        description="This creates a full copy of the project, including the screenplay, breakdown, and assets. It may take a few minutes to complete."
        confirmLabel="DUPLICATE"
        isPending={startDuplicateProjectMutation.isPending}
        onConfirm={() => {
          if (duplicateConfirmTarget) {
            runDuplicateProject(duplicateConfirmTarget.projectId, duplicateConfirmTarget.title);
          }

          setDuplicateConfirmTarget(null);
        }}
      />
      <ConfirmActionAlert
        open={Boolean(copyToProductionConfirmTarget)}
        onClose={() => setCopyToProductionConfirmTarget(null)}
        title={`Migrate "${copyToProductionConfirmTarget?.title ?? ""}" to Production?`}
        description="This creates a full copy of the project, including the screenplay, breakdown, and assets. It may take a few minutes to complete."
        confirmLabel="MIGRATE"
        isPending={startCopyToProductionMutation.isPending}
        onConfirm={() => {
          if (copyToProductionConfirmTarget) {
            runCopyToProduction(copyToProductionConfirmTarget.projectId, copyToProductionConfirmTarget.title);
          }

          setCopyToProductionConfirmTarget(null);
        }}
      />
      <RenameProjectDialog
        open={isNewProjectDialogOpen}
        initialName="untitled"
        allowUnchangedSave
        isSaving={createProjectMutation.isPending}
        onClose={() => setIsNewProjectDialogOpen(false)}
        onSave={async (nextName) => {
          try {
            const { metadata } = store.metaDataStore;
            if (!metadata) {
              toastError("Metadata not loaded yet");
              return;
            }

            const genre = metadata.genre[2]; // Drama
            const targetAudience = metadata.target_audience[0];
            const toneMood = metadata.tone_mood[0];
            if (!genre || !targetAudience || !toneMood) {
              toastError("Metadata is missing required dropdown options");
              return;
            }

            const result = await createProjectMutation.mutateAsync({
              project_name: nextName,
              target_audience: targetAudience,
              genre,
              tone_mood: toneMood,
              style_dna_id: null,
              advanced_image_prompting_guidance: true
            });

            setIsNewProjectDialogOpen(false);

            store.actionStore.clearProjectDetails();
            void navigate(projectPanelPath(result.project_id, DEFAULT_TAB_REDIRECT_PATH));
          } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to create project";
            toastError(message);
          }
        }}
      />
      <RenameProjectDialog
        open={isRenameDialogOpen}
        initialName={renameInitialName}
        isSaving={updateProjectMutation.isPending}
        onClose={() => {
          setIsRenameDialogOpen(false);
          setRenameProjectId(null);
        }}
        onSave={async (nextName) => {
          try {
            const projectId = renameProjectId;
            const data = renameProjectDetails;
            if (!projectId || !data) {
              toastError("Project details not loaded yet");
              return;
            }

            await updateProjectMutation.mutateAsync({
              projectId,
              body: {
                ...data,
                project_name: nextName,
                project: {
                  ...data.project,
                  "project-name": nextName
                }
              }
            });

            setIsRenameDialogOpen(false);
            setRenameProjectId(null);
          } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to rename project";
            toastError(message);
          }
        }}
      />
      <SetThumbnailDialog
        open={isSetThumbnailDialogOpen}
        projectId={setThumbnailProjectId}
        onClose={() => {
          setIsSetThumbnailDialogOpen(false);
          setSetThumbnailProjectId(null);
        }}
      />
    </Box>
  );
};

export default MainPage;
